// U1 · Us tab (QU1_Us): level + the route strip (reached / next / later / coming soon), the place, keepsakes,
// the story, the week and the cards done. Everything comes from state.buddy (route with absolute levels) + state.level.
import { C } from '@shared/content/characters';
import { CHAPTER, STAGE_CAP, STAGE_TITLE } from '@shared/content/story';
import { SEASON_NEXT_LABEL, COMING_SOON_LABEL } from '@shared/rules';
import { useApp } from '../../store';
import { go } from '../../router';
import { Screen, ScrollArea, StreakPill, XScene, XKeep, XCard, Icon, TextButton } from '../../components';
import { STOP_ICON, cardsAway, keepStops, lockedLabel, nextStop, plural, stopName } from './route';
import { US } from './copy';
import s from './us.module.css';

export function UsScreen() {
  const st = useApp((x) => x.state);
  if (!st) return <Screen theme="margo" tab="us" id="U1" />;
  const ch = st.ch, c = C[ch], b = st.buddy, lv = st.level;
  const todo = st.plan.cards.filter((x) => x.st === 'todo').length;
  const route = b.route;
  const next = nextStop(b);
  const ni = next ? next.idx : route.length;
  const start = Math.max(0, Math.min(route.length - 7, ni - 2));
  const strip = route.slice(start, start + 7);
  const away = cardsAway(lv);
  const d = b.daysTogether;

  // Place: progress between the last place stop reached and the next one (in stops, plus this level's fraction).
  const places = route.filter((r) => r.type === 'place');
  const prevPlace = [...places].reverse().find((r) => r.reached) || null;
  const nextPlace = places.find((r) => !r.reached) || null;
  const prevIdx = prevPlace ? prevPlace.idx : -1;
  const stagePct = nextPlace ? Math.min(100, Math.round(((b.stops - (prevIdx + 1)) + lv.into / Math.max(1, lv.need)) / (nextPlace.idx - prevIdx) * 100)) : 100;
  const stage = Math.max(1, Math.min(7, b.stage));

  const keeps = keepStops(b);
  const chapStop = (n: number) => route.find((r) => r.type === 'chap' && r.chap === n) || null;
  const recent = st.collection.recent[0];

  return (
    <Screen theme={ch} tab="us" badge={todo} id="U1">
      <div class={s.glow} aria-hidden="true">
        <XScene ch={ch} stage={stage} mode="blur" video={false} alt="" />
        <div class={s.glowFade} />
      </div>
      <header class={s.head}>
        <div>
          <h1 class={s.h1}>Us</h1>
          <p class={s.sub}>{`${c.name} and you · ${d === 0 ? 'met today' : plural(d, 'day') + ' together'}`}</p>
        </div>
        <StreakPill count={st.streak.count} lit={st.streak.litToday} />
      </header>

      <ScrollArea class={s.usScroll}>
        <div class={s.list}>
          <section class={s.card} aria-label="Your level">
            <div class={s.lvRow}>
              <span class={s.lvBig}>Level {lv.level}</span>
              <span class={s.lvPts}>{`${lv.into} of ${lv.need} points`}</span>
            </div>
            <div class={s.meter} role="progressbar" aria-label="Points to the next level" aria-valuemin={0} aria-valuemax={lv.need} aria-valuenow={lv.into}>
              <div style={{ width: `${Math.round((lv.into / Math.max(1, lv.need)) * 100)}%` }} />
            </div>
            <div class={s.strip} role="img" aria-label={'Your route: ' + strip.map((r) => `level ${r.level}, ${stopName(r)}${r.reached ? ', open' : ''}`).join('; ')}>
              <div class={s.stripLine} aria-hidden="true" />
              <div class={s.stops} aria-hidden="true">
                {strip.map((r) => {
                  const state = r.reached ? 'done' : r === next ? 'next' : r.ready ? 'later' : 'soon';
                  return (
                    <div class={s.stop} key={r.idx}>
                      <span class={s.dot} data-st={state}><Icon name={STOP_ICON[r.type]} size={18} /></span>
                      <span class={s.stopLab} data-st={state}>{r.ready || r.reached ? `Lv ${r.level}` : 'Soon'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div class={s.next}>
              {next && next.ready ? (
                <>
                  <span class={s.nextHead}>{`NEXT · LEVEL ${next.level}`}</span>
                  <span class={s.nextName}>{stopName(next)}</span>
                  <span class={s.nextAway}>{next.level === lv.level + 1 ? `About ${plural(away, 'card')} away` : `Level ${lv.level + 1} is about ${plural(away, 'card')} away`}</span>
                </>
              ) : (
                <>
                  <span class={s.nextHead}>{next ? US.nextHead : US.allOpenHead}</span>
                  <span class={s.nextName}>{next ? COMING_SOON_LABEL : SEASON_NEXT_LABEL}</span>
                  <span class={s.nextAway}>{US.keepGoing}</span>
                </>
              )}
            </div>
            <p class={s.note}>{US.levelNote}</p>
          </section>

          <section class={`${s.card} ${s.place}`} aria-label={c.placeName}>
            <div class={s.thumb}><XScene ch={ch} stage={stage} mode="clear" video={false} drift={false} alt="" w={110} h={196} /></div>
            <div class={s.placeText}>
              <span class={s.placeName}>{c.placeName}</span>
              <span class={s.stageLab}>{`STAGE ${stage} OF 7 · ${STAGE_TITLE[stage - 1].toUpperCase()}`}</span>
              <span class={s.stageCap}>{STAGE_CAP[ch][stage - 1]}</span>
              <span class={s.stageNext}>{!nextPlace ? US.allLooks : nextPlace.ready ? `Changes again at level ${nextPlace.level}` : COMING_SOON_LABEL}</span>
              {nextPlace && nextPlace.ready && <div class={s.stageBar} aria-hidden="true"><div style={{ width: `${stagePct}%` }} /></div>}
            </div>
          </section>

          <section class={s.card} aria-label="Keepsakes">
            <div class={s.rowHead}>
              <span class={s.cardTitle}>{`Keepsakes · ${b.keepsakes.length} of ${keeps.length}`}</span>
              <TextButton tone="accent" weight={800} size={14} style={{ minHeight: 44, padding: '0 4px' }} onClick={() => go('U4')}>See all</TextButton>
            </div>
            <div class={s.keepGrid}>
              {keeps.map((r) => (
                <div class={s.keep} key={r.idx}>
                  <XKeep ch={ch} i={r.keep ?? 0} s={64} locked={!r.reached} />
                  <span class={s.keepName} data-locked={r.reached ? 'false' : 'true'}>{r.reached ? r.label : lockedLabel(r)}</span>
                </div>
              ))}
            </div>
          </section>

          <section class={`${s.card} ${s.story}`} aria-label={`${c.name}'s story`}>
            <p class={s.storyTitle}>{`${c.name}'s story`}</p>
            {CHAPTER[ch].map((title, i) => {
              const n = i + 1;
              if (n <= b.chaptersOpen) {
                return (
                  <button type="button" class={s.chap} key={n} onClick={() => go('U2', { chap: n })}>
                    <span class={s.chapN}>{n}</span>
                    <span class={s.chapT}>{title}</span>
                    {b.newChapter === n && <span class={s.newTag}>NEW</span>}
                    <Icon name="chevron" size={18} class={s.chev} />
                  </button>
                );
              }
              const stop = chapStop(n);
              return (
                <div class={s.chapLocked} key={n}>
                  <span class={s.chapN}>{n}</span>
                  <span class={s.chapLockText}>{stop && stop.ready ? `Opens at level ${stop.level}` : COMING_SOON_LABEL}</span>
                  <Icon name="lock" size={16} color="#8C857B" />
                </div>
              );
            })}
          </section>

          <button type="button" class={s.navRow} onClick={() => go('U3')}>
            <span class={s.navText}><span class={s.cardTitle}>Your week</span><span class={s.navSub}>{st.week && st.week.ready ? US.weekReady : US.weekFirst}</span></span>
            <Icon name="chevron" size={18} class={s.chev} />
          </button>

          <button type="button" class={`${s.navRow} ${s.cardsRow}`} onClick={() => go('U4')}>
            <span class={s.navText}><span class={s.cardTitle}>Cards done</span><span class={s.navSub}>{`${plural(st.collection.cardsDone, 'card')} done so far`}</span></span>
            {recent && <span class={s.mini} aria-hidden="true"><XCard ch={ch} face="front" w={104} h={76} fit="none" title={recent.title} stamped stamp="DONE" foot="" tilt={-3} /></span>}
            <Icon name="chevron" size={18} class={s.chev} />
          </button>
        </div>
      </ScrollArea>
    </Screen>
  );
}
